import { useState, useEffect } from 'react'
import { ChevronUp, ChevronDown, Check } from 'lucide-react'
import { cn } from 'src/enviroment/lib/utils'
import { useInView } from 'react-intersection-observer'

interface FilterDropdownProps {
  type: string
  className?: string
  options: { id: string; name: string }[]
  selectedIds: string[]
  onChange: (selectedIds: string[]) => void
  theme?: 'dark' | 'light'
  onScrollEnd?: () => void
}

const FilterDropdown = ({
  options,
  type,
  selectedIds,
  onChange,
  className,
  theme = 'dark',
  onScrollEnd,
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { ref: sentinelRef, inView } = useInView()

  useEffect(() => {
    if (inView) onScrollEnd?.()
  }, [inView])

  const handleOptionChange = (optionId: string) => {
    const newSelectedIds = selectedIds.includes(optionId)
      ? selectedIds.filter((id) => id !== optionId)
      : [...selectedIds, optionId]
    onChange(newSelectedIds)
  }

  const getSelectedLabels = () => {
    const selected = options.filter((opt) => selectedIds.includes(opt.id)).map((opt) => opt.name)
    if (selected.length === 0) return ''
    return selected.slice(0, 2).join(', ') + (selected.length > 2 ? '...' : '')
  }

  const isDisabled = options.length === 0

  const styles = {
    button: cn(
      'flex w-full items-center justify-between rounded-lg border px-4 py-3',
      theme === 'dark' ? 'text-gray-50 border-gray-700 bg-gray-800' : 'text-gray-600 border-gray-300 bg-white',
      isDisabled && theme === 'dark'
        ? 'cursor-not-allowed border-gray-700 opacity-50'
        : isDisabled && theme === 'light'
        ? 'cursor-not-allowed border-gray-200 opacity-50'
        : 'cursor-pointer',
    ),
    selectedText: cn(theme === 'dark' ? 'text-gray-50' : 'text-gray-900'),
    dropdown: cn(
      'absolute z-50 mt-2 w-full rounded-lg p-2 max-h-60 overflow-y-auto',
      '[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent',
      theme === 'dark'
        ? 'bg-gray-800 [&::-webkit-scrollbar-thumb]:bg-gray-600'
        : 'bg-white border border-gray-200 [&::-webkit-scrollbar-thumb]:bg-gray-300',
    ),
    option: cn(
      'flex cursor-pointer items-center justify-between rounded p-2',
      theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100',
    ),
    optionText: cn('text-sm font-medium', theme === 'dark' ? 'text-gray-300' : 'text-gray-600'),
    checkmark: cn(
      'flex h-6 w-6 items-center justify-center rounded',
      theme === 'dark' ? 'bg-emerald-600' : 'bg-emerald-500',
    ),
  }

  return (
    <div className={cn('relative', className)}>
      <button onClick={() => setIsOpen(!isOpen)} disabled={isDisabled} className={styles.button}>
        <span className="truncate text-sm font-medium">
          {isDisabled ? (
            'No options'
          ) : getSelectedLabels() === '' ? (
            <span className={styles.selectedText}>{type}</span>
          ) : (
            <>
              {type}: <span className={styles.selectedText}>{getSelectedLabels()}</span>
            </>
          )}
        </span>
        {isOpen ? (
          <ChevronUp size={16} className="text-light-green-500" />
        ) : (
          <ChevronDown size={16} className="text-light-green-500" />
        )}
      </button>

      {isOpen && !isDisabled && (
        <div className={styles.dropdown}>
          {options.map((option) => (
            <div key={option.id} onClick={() => handleOptionChange(option.id)} className={styles.option}>
              <span className={styles.optionText}>{option.name}</span>
              {selectedIds.includes(option.id) && (
                <div className={styles.checkmark}>
                  <Check size={16} className="text-white" />
                </div>
              )}
            </div>
          ))}
          {onScrollEnd && <div ref={sentinelRef} className="h-px" />}
        </div>
      )}
    </div>
  )
}

export default FilterDropdown
